import { Controller } from "@hotwired/stimulus"
import { createIcons, icons } from 'lucide'
import confetti from "canvas-confetti"

export default class extends Controller {
  static targets = [
    "chapterContainer",
    "messagesContainer",
    "input",
    "sendButton",
    "feedbackButtonText",
    "currentChapterName",
    "contextPanel",
    "contextBeat",
    "contextGoal",
    "voiceToggle",
    "voiceOnIcon",
    "voiceOffIcon",
    "todosContainer",
    "remainingBadge",
    "remainingCount",
    "chatForm",
    "upgradePrompt"
  ]

  static values = {
    feedbackUrl: String,
    chatUrl: String,
    speakUrl: String,
    updateTodosUrl: String,
    updateHistoryUrl: String,
    remaining: Number,
    limit: Number,
    upgradeUrl: String,
    unitName: { type: String, default: "Chapter" }
  }

  connect() {
    this.conversationHistories = {} // Per-chapter conversation history
    this.chapterTodos = {} // Per-chapter todos
    this.isLoading = false
    this.currentCardId = null
    this.currentChapterId = null
    this.voiceEnabled = true
    this.currentAudio = null

    // Load initial todos and history
    this.loadInitialTodos()
    this.loadInitialHistory()

    // Check if already at limit
    this.checkLimitReached()
  }

  checkLimitReached() {
    if (this.remainingValue === null || this.remainingValue === undefined) return

    if (this.remainingValue <= 0) {
      this.showUpgradePrompt()
    }
  }

  updateRemainingCount(remaining) {
    if (remaining === null || remaining === undefined) return

    this.remainingValue = remaining

    this.remainingCountTargets.forEach(el => {
      el.textContent = remaining
    })

    this.remainingBadgeTargets.forEach(badge => {
      badge.classList.remove("text-gray-500", "text-amber-600", "text-red-600")
      if (remaining <= 3 && remaining > 0) {
        badge.classList.add("text-amber-600")
      } else if (remaining <= 0) {
        badge.classList.add("text-red-600")
      } else {
        badge.classList.add("text-gray-500")
      }
    })

    if (remaining <= 0) {
      this.showUpgradePrompt()
    }
  }

  showUpgradePrompt() {
    if (this.hasChatFormTarget) {
      this.chatFormTarget.classList.add("hidden")
    }
    this.remainingBadgeTargets.forEach(badge => {
      badge.classList.add("hidden")
    })
    if (this.hasUpgradePromptTarget) {
      this.upgradePromptTarget.classList.remove("hidden")
    }
    createIcons({ icons })
  }

  loadInitialTodos() {
    this.todosContainerTargets.forEach(container => {
      const cardId = container.dataset.cardId
      const initialTodos = container.dataset.initialTodos

      if (initialTodos) {
        try {
          const todos = JSON.parse(initialTodos)
          if (Array.isArray(todos) && todos.length > 0) {
            this.chapterTodos[cardId] = todos
            this.renderTodosForChapter(cardId)
          }
        } catch (e) {
          console.warn("Failed to parse initial todos:", e)
        }
      }
    })
  }

  loadInitialHistory() {
    this.todosContainerTargets.forEach(container => {
      const cardId = container.dataset.cardId
      const initialHistory = container.dataset.initialHistory

      if (initialHistory) {
        try {
          const history = JSON.parse(initialHistory)
          if (Array.isArray(history) && history.length > 0) {
            this.conversationHistories[cardId] = history
          }
        } catch (e) {
          console.warn("Failed to parse initial history:", e)
        }
      }
    })
  }

  trackChapter(event) {
    const editor = event.target
    const cardId = editor.dataset.cardId
    const chapterId = editor.dataset.chapterId

    if (cardId && cardId !== this.currentCardId) {
      this.currentCardId = cardId
      this.currentChapterId = chapterId

      // Find chapter container for context
      const container = this.chapterContainerTargets.find(c => c.dataset.cardId === cardId)
      const chapterNum = container?.dataset.cardPosition || "?"

      // Update current chapter display
      if (this.hasCurrentChapterNameTarget) {
        this.currentChapterNameTarget.textContent = `${this.unitNameValue} ${chapterNum}`
      }

      // Update context panel if available
      this.updateContextPanel(container)

      // Enable controls
      this.enableControls()

      // Load conversation history
      this.loadMessagesForChapter(cardId)
    }
  }

  updateContextPanel(container) {
    if (!container || !this.hasContextPanelTarget) return

    // Show context panel
    this.contextPanelTarget.classList.remove("hidden")

    // Could extract beat and goal from container data attributes if needed
  }

  enableControls() {
    if (this.hasInputTarget) {
      this.inputTarget.disabled = false
    }
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = false
    }
  }

  disableControls() {
    if (this.hasInputTarget) {
      this.inputTarget.disabled = true
    }
    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = true
    }
  }

  loadMessagesForChapter(cardId) {
    const container = this.messagesContainerTarget
    if (!container) return

    container.innerHTML = ""

    const history = this.conversationHistories[cardId] || []

    if (history.length === 0) {
      const unitLower = this.unitNameValue.toLowerCase()
      container.innerHTML = `
        <div class="text-center text-gray-400 text-sm py-8">
          <p>Click "Get Feedback" on a ${unitLower} to get writing help.</p>
        </div>
      `
    } else {
      history.forEach(msg => {
        this.addMessageToContainer(msg.content, msg.role, container)
      })
    }
  }

  requestFeedback(event) {
    const cardId = event.currentTarget.dataset.cardId
    const chapterId = event.currentTarget.dataset.chapterId

    if (!cardId || this.isLoading) return

    // Set current chapter
    this.currentCardId = cardId
    this.currentChapterId = chapterId

    // Update UI
    const container = this.chapterContainerTargets.find(c => c.dataset.cardId === cardId)
    const chapterNum = container?.dataset.cardPosition || "?"

    if (this.hasCurrentChapterNameTarget) {
      this.currentChapterNameTarget.textContent = `${this.unitNameValue} ${chapterNum}`
    }

    this.enableControls()
    this.loadMessagesForChapter(cardId)

    // Request feedback
    this.requestFeedbackInternal(cardId)
  }

  requestFeedbackInternal(cardId) {
    if (this.isLoading) return

    this.setLoading(true)
    this.addMessage("Ready for feedback", "user")

    const formData = new FormData()
    formData.append("card_id", cardId)
    formData.append("history", JSON.stringify(this.conversationHistories[cardId] || []))

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(this.feedbackUrlValue, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest"
      },
      credentials: "same-origin"
    })
      .then(response => response.json())
      .then(data => {
        if (data.limit_reached) {
          this.addMessage(data.error, "error")
          this.updateRemainingCount(0)
        } else if (data.error) {
          this.addMessage(data.error, "error")
        } else {
          this.addMessage(data.message, "assistant")

          if (!this.conversationHistories[cardId]) {
            this.conversationHistories[cardId] = []
          }
          this.conversationHistories[cardId].push(
            { role: "user", content: "[Chapter content for feedback]" },
            { role: "assistant", content: data.message }
          )

          // Handle todos
          if (data.todos && data.todos.length > 0) {
            this.setTodosForChapter(cardId, data.todos)
          }

          // Auto-check completed todos
          if (data.completed_indices && data.completed_indices.length > 0) {
            this.autoCompleteTodos(cardId, data.completed_indices)
          }

          // Update remaining count
          if (data.remaining !== undefined) {
            this.updateRemainingCount(data.remaining)
          }

          // Persist history
          this.persistHistory(cardId)

          // Update button text to "Get Feedback" since we got a response
          this.updateFeedbackButtonText(cardId, "Get Feedback")
        }
      })
      .catch(error => {
        console.error("Feedback error:", error)
        this.addMessage("Something went wrong. Please try again.", "error")
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  updateFeedbackButtonText(cardId, text) {
    const buttonText = this.feedbackButtonTextTargets.find(el => el.dataset.cardId === cardId)
    if (buttonText) {
      buttonText.textContent = text
    }
  }

  // Called from editor input to check if button text should change
  checkWordCount(event) {
    const editor = event.target
    const cardId = editor.dataset.cardId

    if (!cardId) return

    // Get word count from editor content
    const content = editor.value || editor.innerText || ""
    const wordCount = content.trim().split(/\s+/).filter(w => w.length > 0).length

    // If more than 3 words, change to "Get Feedback"
    if (wordCount > 3) {
      this.updateFeedbackButtonText(cardId, "Get Feedback")
    }
  }

  sendMessage(event) {
    event?.preventDefault()

    if (this.isLoading || !this.currentCardId) return

    const cardId = this.currentCardId
    const input = this.inputTarget
    const message = input.value.trim()

    if (!message) return

    input.value = ""
    this.addMessage(message, "user")
    this.setLoading(true)

    const history = this.conversationHistories[cardId] || []

    const formData = new FormData()
    formData.append("message", message)
    formData.append("card_id", cardId)
    formData.append("history", JSON.stringify(history))

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(this.chatUrlValue, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": csrfToken,
        "X-Requested-With": "XMLHttpRequest"
      },
      credentials: "same-origin"
    })
      .then(response => response.json())
      .then(data => {
        if (data.limit_reached) {
          this.addMessage(data.error, "error")
          this.updateRemainingCount(0)
        } else if (data.error) {
          this.addMessage(data.error, "error")
        } else {
          this.addMessage(data.message, "assistant")

          if (!this.conversationHistories[cardId]) {
            this.conversationHistories[cardId] = []
          }
          this.conversationHistories[cardId].push(
            { role: "user", content: message },
            { role: "assistant", content: data.message }
          )

          if (data.remaining !== undefined) {
            this.updateRemainingCount(data.remaining)
          }

          this.persistHistory(cardId)
        }
      })
      .catch(error => {
        console.error("Chat error:", error)
        this.addMessage("Something went wrong. Please try again.", "error")
      })
      .finally(() => {
        this.setLoading(false)
      })
  }

  handleKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.sendMessage({ preventDefault: () => {} })
    }
  }

  addMessage(content, role) {
    const container = this.messagesContainerTarget
    if (!container) return

    const placeholder = container.querySelector('.text-center.py-8')
    if (placeholder) placeholder.remove()

    this.addMessageToContainer(content, role, container)

    if (role === "assistant" && this.voiceEnabled) {
      this.speak(content)
    }
  }

  addMessageToContainer(content, role, container) {
    const messageDiv = document.createElement("div")
    messageDiv.className = this.getMessageClasses(role)

    const contentP = document.createElement("p")
    contentP.className = "text-sm whitespace-pre-wrap"
    contentP.textContent = content

    messageDiv.appendChild(contentP)
    container.appendChild(messageDiv)

    container.scrollTop = container.scrollHeight
  }

  getMessageClasses(role) {
    const baseClasses = "rounded-lg px-3 py-2"

    switch (role) {
      case "user":
        return `${baseClasses} bg-primary-100 text-primary-900 ml-8`
      case "assistant":
        return `${baseClasses} bg-white text-gray-800 border border-gray-200 mr-8`
      case "error":
        return `${baseClasses} bg-red-100 text-red-700`
      default:
        return baseClasses
    }
  }

  setLoading(loading) {
    this.isLoading = loading

    if (this.hasSendButtonTarget) {
      this.sendButtonTarget.disabled = loading
    }

    if (this.hasInputTarget) {
      this.inputTarget.disabled = loading
    }

    if (loading) {
      this.showTypingIndicator()
    } else {
      this.hideTypingIndicator()
    }
  }

  showTypingIndicator() {
    const container = this.messagesContainerTarget
    if (!container) return

    const indicator = document.createElement("div")
    indicator.id = "typing-indicator"
    indicator.className = "flex items-center gap-1 text-gray-400 text-sm px-3 py-2"
    indicator.innerHTML = `
      <span class="animate-pulse">Thinking</span>
      <span class="animate-bounce" style="animation-delay: 0ms">.</span>
      <span class="animate-bounce" style="animation-delay: 150ms">.</span>
      <span class="animate-bounce" style="animation-delay: 300ms">.</span>
    `

    container.appendChild(indicator)
    container.scrollTop = container.scrollHeight
  }

  hideTypingIndicator() {
    const indicator = document.getElementById("typing-indicator")
    indicator?.remove()
  }

  toggleVoice() {
    this.voiceEnabled = !this.voiceEnabled

    if (this.hasVoiceToggleTarget) {
      this.voiceToggleTarget.title = this.voiceEnabled ? "Toggle voice (on)" : "Toggle voice (off)"
    }

    if (this.hasVoiceOnIconTarget && this.hasVoiceOffIconTarget) {
      this.voiceOnIconTarget.classList.toggle("hidden", !this.voiceEnabled)
      this.voiceOffIconTarget.classList.toggle("hidden", this.voiceEnabled)
    }

    if (!this.voiceEnabled) {
      this.stopSpeaking()
    }
  }

  async speak(text) {
    if (!this.voiceEnabled) return
    if (!this.speakUrlValue) return

    this.stopSpeaking()

    // Split text into sentences for chunked playback
    const sentences = this.splitIntoSentences(text)
    if (sentences.length === 0) return

    this.audioQueue = []
    this.isPlayingQueue = true

    // Start fetching and playing chunks
    this.playChunkedAudio(sentences)
  }

  splitIntoSentences(text) {
    // Split on sentence endings, keeping reasonable chunk sizes
    const chunks = text.match(/[^.!?]+[.!?]+\s*/g) || [text]

    // Combine very short chunks with the next one
    const result = []
    let current = ""

    for (const chunk of chunks) {
      current += chunk
      if (current.length >= 50) {
        result.push(current.trim())
        current = ""
      }
    }

    if (current.trim()) {
      result.push(current.trim())
    }

    return result.length > 0 ? result : [text]
  }

  async playChunkedAudio(sentences) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    for (let i = 0; i < sentences.length; i++) {
      if (!this.isPlayingQueue) break

      const sentence = sentences[i]

      try {
        const response = await fetch(this.speakUrlValue, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "X-CSRF-Token": csrfToken,
            "X-Requested-With": "XMLHttpRequest"
          },
          credentials: "same-origin",
          body: new URLSearchParams({ text: sentence })
        })

        if (!response.ok || !this.isPlayingQueue) continue

        const audioBlob = await response.blob()
        const audioUrl = URL.createObjectURL(audioBlob)

        await this.playAudioChunk(audioUrl)
        URL.revokeObjectURL(audioUrl)
      } catch (error) {
        console.error("TTS chunk error:", error)
      }
    }

    this.isPlayingQueue = false
  }

  playAudioChunk(audioUrl) {
    return new Promise((resolve) => {
      if (!this.isPlayingQueue) {
        resolve()
        return
      }

      this.currentAudio = new Audio(audioUrl)
      this.currentAudio.onended = () => {
        this.currentAudio = null
        resolve()
      }
      this.currentAudio.onerror = () => {
        this.currentAudio = null
        resolve()
      }
      this.currentAudio.play().catch(() => resolve())
    })
  }

  stopSpeaking() {
    this.isPlayingQueue = false
    if (this.currentAudio) {
      this.currentAudio.pause()
      this.currentAudio.currentTime = 0
      this.currentAudio = null
    }
  }

  // Todo management
  setTodosForChapter(cardId, todoTexts) {
    this.chapterTodos[cardId] = todoTexts.map(text => ({
      text: text,
      completed: false
    }))
    this.renderTodosForChapter(cardId)
  }

  renderTodosForChapter(cardId) {
    const container = this.todosContainerTargets.find(c => c.dataset.cardId === cardId)
    if (!container) return

    const todos = this.chapterTodos[cardId] || []

    if (todos.length === 0) {
      container.classList.add("hidden")
      container.innerHTML = ""
      return
    }

    container.classList.remove("hidden")
    container.innerHTML = ""

    todos.forEach((todo, index) => {
      const isCompleted = todo.completed === true || todo.completed === "true"

      const todoEl = document.createElement("label")
      todoEl.className = `inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm cursor-pointer transition-all ${
        isCompleted
          ? "bg-gray-100 text-gray-400 line-through"
          : "bg-amber-100 text-amber-800 hover:bg-amber-200"
      }`

      const checkbox = document.createElement("input")
      checkbox.type = "checkbox"
      checkbox.checked = isCompleted
      checkbox.className = "w-3.5 h-3.5 rounded accent-amber-600"
      checkbox.addEventListener("change", () => this.toggleTodo(cardId, index))

      const textSpan = document.createElement("span")
      textSpan.textContent = todo.text

      todoEl.appendChild(checkbox)
      todoEl.appendChild(textSpan)
      container.appendChild(todoEl)
    })
  }

  toggleTodo(cardId, index) {
    const key = String(cardId)
    if (!this.chapterTodos[key] || !this.chapterTodos[key][index]) return

    const current = this.chapterTodos[key][index].completed === true || this.chapterTodos[key][index].completed === "true"
    this.chapterTodos[key][index].completed = !current
    this.renderTodosForChapter(key)
    this.persistTodos(key)
  }

  async persistTodos(cardId) {
    if (!this.updateTodosUrlValue) return

    const todos = this.chapterTodos[cardId] || []
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      await fetch(this.updateTodosUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body: JSON.stringify({ card_id: cardId, chapter: { coach_todos: todos } })
      })
    } catch (error) {
      console.error("Failed to persist todos:", error)
    }
  }

  async persistHistory(cardId) {
    if (!this.updateHistoryUrlValue) return

    const history = this.conversationHistories[cardId] || []
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      await fetch(this.updateHistoryUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body: JSON.stringify({ card_id: cardId, history: JSON.stringify(history) })
      })
    } catch (error) {
      console.error("Failed to persist history:", error)
    }
  }

  autoCompleteTodos(cardId, completedIndices) {
    const key = String(cardId)
    if (!this.chapterTodos[key]) return

    let anyCompleted = false

    completedIndices.forEach(index => {
      if (this.chapterTodos[key][index] && !this.chapterTodos[key][index].completed) {
        this.chapterTodos[key][index].completed = true
        anyCompleted = true
      }
    })

    if (anyCompleted) {
      this.renderTodosForChapter(key)
      this.persistTodos(key)
      this.fireSmallConfetti()
    }
  }

  fireSmallConfetti() {
    const colors = ['#f97316', '#fb923c', '#fbbf24', '#10b981']

    confetti({
      particleCount: 30,
      spread: 60,
      origin: { y: 0.7 },
      colors: colors,
      ticks: 100,
      gravity: 1.2,
      scalar: 0.8
    })
  }
}
